import { useQuery, useQueryClient } from '@tanstack/react-query';
import StorageProviderService from '@utility/services/storage-provider';
import type { RegionTree, ShippingTemplate } from '../../lib/shipping-template';

export type ProviderMarketplace = {
  marketplace_id: string;
  alias: string;
  country_code: string;
};

const noRegions: RegionTree = [];
const noMarketplaces: ProviderMarketplace[] = [];

const templateKey = (providerId: string) =>
  ['storage-providers', providerId, 'shipping-template'] as const;

/** The template, the region tree its rows pick from and the provider's marketplaces. */
export function useShippingTemplate(providerId: string) {
  const queryClient = useQueryClient();

  const template = useQuery({
    queryKey: templateKey(providerId),
    queryFn: async () =>
      (await StorageProviderService.getShippingTemplate(providerId))
        ?.data as ShippingTemplate,
    enabled: !!providerId,
  });
  const regions = useQuery({
    queryKey: ['storage-providers', 'shipping-template-regions'],
    queryFn: async () =>
      (await StorageProviderService.shippingTemplateRegion())
        ?.data as RegionTree,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const marketplaces = useQuery({
    queryKey: ['storage-providers', providerId, 'marketplaces'],
    queryFn: async () =>
      (await StorageProviderService.marketplaces(providerId))
        ?.data as ProviderMarketplace[],
    enabled: !!providerId,
  });

  return {
    template: template.data,
    regions: regions.data ?? noRegions,
    marketplaces: marketplaces.data ?? noMarketplaces,
    loading: template.isPending || regions.isPending || marketplaces.isPending,
    save: async (payload: unknown) => {
      const result = await StorageProviderService.updateShippingTemplate(
        providerId,
        payload,
      );
      queryClient.setQueryData(templateKey(providerId), result.data);
    },
  };
}
